// The input contract (decision 0032): every action declares its value type and its context, and binds controls on
// three device families: keyboard and mouse, touch, and (optionally) gamepad. `checkContract` reports any action
// missing a required family, a binding whose shape doesn't fit its action's type, a control name no device writes,
// and two actions sharing a control in one context. `compileContract` turns the contract into the `ActionMap` that
// `Actions` reads. The contract is plain data the game passes in (spike 002 builds it from its content tables).
//
// Control names are `Controls`' (controls.ts): KeyboardEvent.code for keys, "Mouse0".."Mouse4", the pointer deltas
// "PointerDrag", "PointerMove", "PointerLocked", "Wheel", the W3C standard pad "Pad0".."Pad16", "PadAxis<n>+/-",
// "PadLeft", "PadRight", and "Touch:<name>" for anything an on-screen layer writes (a button, a stick, a drag).
import type { ActionMap, DeltaBinding, StickBinding } from "./actions";

/** What an action reads as: a press (0..1), a 2D direction in the unit disc, or accumulated 2D motion. */
export type ValueType = "button" | "axis2" | "delta2";
/** The device a binding row is for. Keyboard and mouse are one family. */
export type Device = "keyboard" | "mouse" | "gamepad" | "touch";
/** The three device families the contract asks every action to cover. */
export type DeviceFamily = "keyboardMouse" | "gamepad" | "touch";
/** How a binding's controls make the action's value: any of them pressed; four keys (up, down, left, right); an
 *  analogue vector; accumulated pointer motion times `scale`; a vector turned into a rate per second times `scale`. */
export type BindingShape = "button" | "dpad" | "vector" | "pointer" | "rate";
/** What a control carries. Touch controls may be any of them: the touch layer decides (see `checkTouchLayout`). */
export type ControlKind = "button" | "vector" | "delta" | "any";

/** Every family, in prompt fallback order. */
export const DEVICE_FAMILIES: readonly DeviceFamily[] = ["keyboardMouse", "gamepad", "touch"];

/** The W3C standard gamepad mapping's controls by common name (for readable data and glyph tables). */
export const STANDARD_PAD = {
  south: "Pad0",
  east: "Pad1",
  west: "Pad2",
  north: "Pad3",
  leftBumper: "Pad4",
  rightBumper: "Pad5",
  leftTrigger: "Pad6",
  rightTrigger: "Pad7",
  select: "Pad8",
  start: "Pad9",
  leftStickPress: "Pad10",
  rightStickPress: "Pad11",
  dpadUp: "Pad12",
  dpadDown: "Pad13",
  dpadLeft: "Pad14",
  dpadRight: "Pad15",
  home: "Pad16",
  leftStick: "PadLeft",
  rightStick: "PadRight",
} as const;

/** One action the game reads. */
export interface ActionDecl {
  id: string;
  type: ValueType;
  /** The context it belongs to (see `ContextDecl`). */
  context: string;
  /** The player may choose hold or toggle for it (decision 0063). */
  holdOrToggle?: boolean;
  /** Families this action deliberately doesn't bind, each with the reason (reported, not failed). */
  waive?: Partial<Record<DeviceFamily, string>>;
  /** Bound to another action's controls instead of its own (a glide on the jump's: press again in the air). It has
   *  no bindings of its own, follows the other's rebinding, covers the other's families, and sharing with it is not
   *  a conflict. Same context and value type; the other must not share in turn. */
  shares?: string;
}

/** One default binding: a device's controls for one action. Rebinding replaces `controls` by `id`. */
export interface BindingDecl {
  id: string;
  action: string;
  device: Device;
  /** Default: "button" for buttons; "dpad" for an axis2 with four controls, else "vector"; "pointer" for a delta2. */
  shape?: BindingShape;
  /** For "dpad": exactly four, in the order up, down, left, right. */
  controls: readonly string[];
  /** Per-axis scale: pixels to units for "pointer", units per second for "rate". */
  scale?: readonly [number, number];
}

/** A named, layered binding set. Active contexts are read from the highest priority down; one that `blocks` hides
 *  every context below it. A `base` context is always active. */
export interface ContextDecl {
  id: string;
  priority: number;
  blocks?: boolean;
  base?: boolean;
}

/** The whole contract a game passes in. */
export interface InputContract {
  actions: readonly ActionDecl[];
  bindings: readonly BindingDecl[];
  contexts: readonly ContextDecl[];
}

/** One problem `checkContract` found. Errors fail the check; warnings and notes don't. */
export interface ContractIssue {
  kind: "missing-family" | "waived" | "bad-shape" | "bad-control" | "unknown-action" | "unknown-context" | "duplicate-id" | "conflict" | "bad-share";
  severity: "error" | "warning" | "note";
  action?: string;
  binding?: string;
  message: string;
}

/** What `checkContract` returns. */
export interface ContractCheck {
  ok: boolean;
  issues: ContractIssue[];
  /** The families each action is bound on. */
  families: Record<string, DeviceFamily[]>;
}

/** Which families must be bound (an error when missing) and which may be (a warning). Default: keyboard and mouse
 *  and touch required, gamepad optional (decision 0032). */
export interface CheckOptions {
  required?: readonly DeviceFamily[];
  optional?: readonly DeviceFamily[];
}

/** The family a device belongs to. */
export const familyOf = (d: Device): DeviceFamily => (d === "keyboard" || d === "mouse" ? "keyboardMouse" : d);

const MOUSE_DELTAS = new Set(["PointerDrag", "PointerMove", "PointerLocked", "Wheel"]);

/** What device writes a control and what it carries; null for a name no device writes. */
export function controlInfo(control: string): { device: Device; family: DeviceFamily; kind: ControlKind } | null {
  if (control.startsWith("Touch:")) return control.length > 6 ? { device: "touch", family: "touch", kind: "any" } : null;
  if (/^Pad(\d|1[0-6])$/.test(control) || /^PadAxis\d[+-]$/.test(control)) return { device: "gamepad", family: "gamepad", kind: "button" };
  if (control === "PadLeft" || control === "PadRight") return { device: "gamepad", family: "gamepad", kind: "vector" };
  if (/^Mouse[0-4]$/.test(control)) return { device: "mouse", family: "keyboardMouse", kind: "button" };
  if (MOUSE_DELTAS.has(control)) return { device: "mouse", family: "keyboardMouse", kind: "delta" };
  if (/^(Pad|Mouse|Pointer|Touch)/.test(control)) return null;
  if (/^[A-Z][A-Za-z0-9]+$/.test(control)) return { device: "keyboard", family: "keyboardMouse", kind: "button" };
  return null;
}

const SHAPES: Record<ValueType, readonly BindingShape[]> = { button: ["button"], axis2: ["dpad", "vector"], delta2: ["pointer", "rate"] };
const SHAPE_KIND: Record<BindingShape, ControlKind> = { button: "button", dpad: "button", vector: "vector", pointer: "delta", rate: "vector" };

/** A binding's shape, with the default filled in. */
export function shapeOf(b: BindingDecl, type: ValueType): BindingShape {
  if (b.shape) return b.shape;
  if (type === "button") return "button";
  if (type === "axis2") return b.controls.length === 4 ? "dpad" : "vector";
  return "pointer";
}

/** The controls a binding really uses (a dpad's empty slots left out). */
const used = (b: BindingDecl): string[] => b.controls.filter((c) => c !== "");

/** Check a contract. `ok` is false on any error. */
export function checkContract(c: InputContract, o: CheckOptions = {}): ContractCheck {
  const required = o.required ?? ["keyboardMouse", "touch"];
  const optional = o.optional ?? ["gamepad"];
  const issues: ContractIssue[] = [];
  const families: Record<string, DeviceFamily[]> = {};
  const dup = (what: string, ids: readonly string[]): void => {
    const seen = new Set<string>();
    for (const id of ids) {
      if (seen.has(id)) issues.push({ kind: "duplicate-id", severity: "error", message: `${what} "${id}" is declared twice` });
      seen.add(id);
    }
  };
  dup("context", c.contexts.map((x) => x.id));
  dup("action", c.actions.map((x) => x.id));
  dup("binding", c.bindings.map((x) => x.id));

  const contexts = new Set(c.contexts.map((x) => x.id));
  const actions = new Map(c.actions.map((a) => [a.id, a]));
  for (const a of c.actions) if (!contexts.has(a.context)) issues.push({ kind: "unknown-context", severity: "error", action: a.id, message: `action "${a.id}" is in unknown context "${a.context}"` });
  for (const a of c.actions) {
    if (a.shares === undefined) continue;
    const t = actions.get(a.shares);
    const bad = (why: string): void => void issues.push({ kind: "bad-share", severity: "error", action: a.id, message: `action "${a.id}" shares "${a.shares}": ${why}` });
    if (!t) bad("no such action");
    else if (t.id === a.id || t.shares !== undefined) bad("it shares in turn (share an action with bindings of its own)");
    else if (t.context !== a.context) bad(`it is in context "${t.context}", not "${a.context}"`);
    else if (t.type !== a.type) bad(`it is a ${t.type}, not a ${a.type}`);
    if (c.bindings.some((b) => b.action === a.id)) bad("an action that shares another's controls has no bindings of its own");
  }

  for (const b of c.bindings) {
    const a = actions.get(b.action);
    if (!a) {
      issues.push({ kind: "unknown-action", severity: "error", binding: b.id, message: `binding "${b.id}" is for unknown action "${b.action}"` });
      continue;
    }
    const shape = shapeOf(b, a.type);
    if (!SHAPES[a.type].includes(shape))
      issues.push({ kind: "bad-shape", severity: "error", action: a.id, binding: b.id, message: `binding "${b.id}": shape "${shape}" doesn't fit a ${a.type} action (use ${SHAPES[a.type].join(" or ")})` });
    else if (shape === "dpad" ? b.controls.length !== 4 || used(b).length === 0 : used(b).length === 0)
      issues.push({ kind: "bad-shape", severity: "error", action: a.id, binding: b.id, message: `binding "${b.id}": ${shape === "dpad" ? "a dpad takes four controls (up, down, left, right)" : "no controls"}` });
    else if ((shape === "pointer" || shape === "rate") && !(b.scale && b.scale.length === 2 && b.scale.every(Number.isFinite)))
      issues.push({ kind: "bad-shape", severity: "error", action: a.id, binding: b.id, message: `binding "${b.id}": a ${shape} binding needs a scale [x, y]` });
    for (const ctl of used(b)) {
      const info = controlInfo(ctl);
      const want = SHAPE_KIND[shape];
      if (!info) issues.push({ kind: "bad-control", severity: "error", action: a.id, binding: b.id, message: `binding "${b.id}": no device writes "${ctl}"` });
      else if (info.family !== familyOf(b.device))
        issues.push({ kind: "bad-control", severity: "error", action: a.id, binding: b.id, message: `binding "${b.id}": "${ctl}" is a ${info.device} control, not ${b.device}` });
      else if (info.kind !== "any" && info.kind !== want)
        issues.push({ kind: "bad-control", severity: "error", action: a.id, binding: b.id, message: `binding "${b.id}": "${ctl}" is a ${info.kind}, a ${shape} binding needs a ${want}` });
    }
  }

  for (const a of c.actions) {
    const own = a.shares ?? a.id;
    const fam = [...new Set(c.bindings.filter((b) => b.action === own && used(b).length > 0).map((b) => familyOf(b.device)))];
    families[a.id] = DEVICE_FAMILIES.filter((f) => fam.includes(f));
    for (const f of [...required, ...optional]) {
      if (fam.includes(f)) continue;
      const why = a.waive?.[f];
      if (why) issues.push({ kind: "waived", severity: "note", action: a.id, message: `action "${a.id}" has no ${f} binding by design: ${why}` });
      else if (required.includes(f)) issues.push({ kind: "missing-family", severity: "error", action: a.id, message: `action "${a.id}" has no ${f} binding` });
      else issues.push({ kind: "missing-family", severity: "warning", action: a.id, message: `action "${a.id}" has no ${f} binding (optional)` });
    }
  }

  for (const k of conflicts(c)) issues.push({ kind: "conflict", severity: "error", action: k.actions[0], message: `"${k.control}" is bound to ${k.actions.map((x) => `"${x}"`).join(" and ")} in context "${k.context}"` });

  return { ok: !issues.some((i) => i.severity === "error"), issues, families };
}

/** Two or more actions of one context sharing a control. */
export interface Conflict {
  control: string;
  context: string;
  actions: string[];
  bindings: string[];
}

/** Every control two actions of the same context share (a higher context overriding a lower one is not a conflict:
 *  it is how layering works; nor is an action declared to share another's controls, which has no bindings). */
export function conflicts(c: InputContract): Conflict[] {
  const actions = new Map(c.actions.map((a) => [a.id, a]));
  const byKey = new Map<string, { actions: Set<string>; bindings: string[] }>();
  for (const b of c.bindings) {
    const a = actions.get(b.action);
    if (!a) continue;
    for (const ctl of used(b)) {
      const key = `${a.context}\u0000${ctl}`;
      const e = byKey.get(key) ?? { actions: new Set(), bindings: [] };
      e.actions.add(a.id);
      e.bindings.push(b.id);
      byKey.set(key, e);
    }
  }
  const out: Conflict[] = [];
  for (const [key, e] of byKey) {
    if (e.actions.size < 2) continue;
    const [context, control] = key.split("\u0000") as [string, string];
    out.push({ control, context, actions: [...e.actions], bindings: e.bindings });
  }
  return out;
}

/** A readable line for a log. */
export const formatIssue = (i: ContractIssue): string => `[input contract] ${i.severity}: ${i.message}`;

/** Turn bindings into the `ActionMap` `Actions` reads (only the actions listed; every listed action gets an entry,
 *  so an action with all its controls taken by a higher context reads idle rather than unknown). */
export function compileContract(actions: readonly ActionDecl[], bindings: readonly BindingDecl[]): ActionMap {
  const map: Required<ActionMap> = { buttons: {}, sticks: {}, deltas: {} };
  const byAction = new Map<string, BindingDecl[]>();
  for (const b of bindings) byAction.set(b.action, [...(byAction.get(b.action) ?? []), b]);
  for (const a of actions) {
    const bs = byAction.get(a.shares ?? a.id) ?? [];
    if (a.type === "button") {
      map.buttons[a.id] = [...new Set(bs.flatMap(used))];
    } else if (a.type === "axis2") {
      const s: Required<StickBinding> = { up: [], down: [], left: [], right: [], vectors: [] };
      for (const b of bs) addStick(s, b, shapeOf(b, a.type));
      map.sticks[a.id] = s;
    } else {
      const d: DeltaBinding = {};
      for (const b of bs) {
        const shape = shapeOf(b, a.type);
        const scale: [number, number] = [b.scale?.[0] ?? 1, b.scale?.[1] ?? 1];
        if (shape === "pointer") d.pointer = [...(d.pointer ?? []), ...used(b).map((control) => ({ control, scale }))];
        else if (shape === "rate") {
          const s = d.stick ?? { up: [], down: [], left: [], right: [], vectors: [], rate: scale };
          s.vectors = [...(s.vectors ?? []), ...used(b)];
          d.stick = s;
        }
      }
      map.deltas[a.id] = d;
    }
  }
  return map;
}

function addStick(s: Required<StickBinding>, b: BindingDecl, shape: BindingShape): void {
  if (shape === "dpad") {
    const [up, down, left, right] = b.controls;
    if (up) s.up.push(up);
    if (down) s.down.push(down);
    if (left) s.left.push(left);
    if (right) s.right.push(right);
  } else s.vectors.push(...used(b));
}

/** The glyph-table keys for a binding, most specific first: its whole control list joined by ".", then each control. */
export const glyphKeys = (b: BindingDecl): string[] => [used(b).join("."), ...used(b)];

/** A prompt's binding and glyph for one action on one family (null when the action has no binding there or the table
 *  has no glyph for it). `table` maps a glyph key (see `glyphKeys`) to whatever the game shows: a string id, an icon. */
export function glyphFor<G>(c: InputContract, action: string, family: DeviceFamily, table: Readonly<Record<string, G>>): { binding: BindingDecl; key: string; glyph: G } | null {
  const own = c.actions.find((a) => a.id === action)?.shares ?? action;
  for (const b of c.bindings) {
    if (b.action !== own || familyOf(b.device) !== family || used(b).length === 0) continue;
    for (const key of glyphKeys(b)) if (key in table) return { binding: b, key, glyph: table[key]! };
  }
  return null;
}
